/**
 * Steps 1 and 2: what the client wants to achieve (in their own words, then an outcome), and the mix: any single part
 * or any combination, each part with its own goal in that channel's words.
 */
import { useState } from 'react';
import { Check, ChevronDown, Plus, X } from 'lucide-react';
import { cn } from '@/components/ui/utils';
import type { OrderPrepare } from '../types';
import type { Catalogue, PartId } from './types';
import { goalOf, withParts, type V5State } from './state';
import { PartIcon } from './Visuals';
import { OUTCOME_ICON } from './parts';
import { Card, H1, H2, Pills, TextCommit } from './ui';

export interface StepProps {
  prep: OrderPrepare; cat: Catalogue; s: V5State;
  set: (patch: Partial<V5State>, wrote?: { field: string; value: string }) => void;
}

const titles = (cat: Catalogue, ids: PartId[]) => ids.map((id) => cat.parts.find((p) => p.id === id)!.title).join(' + ');

export function OutcomeStep({ prep, cat, s, set }: StepProps) {
  const pick = (id: string) => {
    const o = cat.outcomes.find((x) => x.id === id)!;
    const parts = withParts(cat, o.mix);
    set({ outcome: id, parts, usedSuggestion: true, goals: {} }, { field: 'Outcome', value: o.title });
  };
  return (
    <>
      <H1 title="What do you want to achieve?" sub="Tell us what you want to get out there, in your own words, even if it is not on your website yet. Then pick the outcome." />
      <Card>
        <TextCommit id="v5-brief" area value={s.brief} onCommit={(v) => {
            // a Brand OS product named in their words becomes the product
            const named = prep.products.find((x) => v.toLowerCase().includes(x.name.toLowerCase()));
            set({ brief: v, ...(named ? { productName: named.name } : {}) }, v ? { field: 'Your words', value: v.slice(0, 80) } : undefined);
          }}
          placeholder={`${prep.products[0] ? `Our ${prep.products[0].name}. ` : ''}What is new, when it launches, where, and what it should do.`} />
      </Card>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cat.outcomes.map((o) => {
          const Icon = OUTCOME_ICON[o.id]; const on = s.outcome === o.id;
          return (
            <button key={o.id} type="button" onClick={() => pick(o.id)} aria-pressed={on}
              className={cn('flex flex-col items-start gap-1.5 rounded-2xl border bg-white p-5 text-left shadow-sm transition-all', on ? 'border-[#be43ff] bg-[#be43ff]/[0.04] shadow-sm' : 'border-[#eaecf0] hover:border-[#be43ff]/40 hover:bg-[#fcf7ff]')}>
              <span className={cn('mb-2 grid h-10 w-10 place-items-center rounded-xl', on ? 'bg-[#be43ff] text-white' : 'bg-[#be43ff]/8 text-[#be43ff]')}>{Icon && <Icon className="h-5 w-5" aria-hidden />}</span>
              <span className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{o.title}</span>
              <span className="text-[13px] text-[#344054]">{o.line}</span>
              <span className="text-[11.5px] leading-snug text-[#667085]">{titles(cat, o.mix)}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

export function MixStep({ cat, s, set }: StepProps) {
  const [open, setOpen] = useState(false);
  const outcome = cat.outcomes.find((o) => o.id === s.outcome);
  const suggested = outcome ? withParts(cat, outcome.mix) : [];
  const added = s.parts.filter((p) => !suggested.includes(p));
  const isSuggested = suggested.length > 0 && suggested.every((p) => s.parts.includes(p));
  const toggle = (id: PartId) => {
    const on = s.parts.includes(id);
    const next = on ? s.parts.filter((p) => p !== id) : withParts(cat, [...s.parts, id]);
    // a boost with nothing left to boost goes with its source
    const kept = next.filter((p) => { const part = cat.parts.find((x) => x.id === p)!; return !part.needs || part.needs.some((n) => next.includes(n)); });
    set({ parts: kept }, { field: on ? 'Removed' : 'Added', value: cat.parts.find((p) => p.id === id)!.title });
  };
  const groups: Array<[string, Catalogue['parts']]> = [
    ['Creators', cat.parts.filter((p) => p.offering === 'creators')],
    ['Amplification', cat.parts.filter((p) => p.offering === 'amplification')],
    ['Measurement', cat.parts.filter((p) => p.offering === 'measurement')],
  ];
  return (
    <>
      <H1 title="Your mix" sub={`Any single part, or any combination.${outcome ? ` Lykke starts from what works for “${outcome.title.toLowerCase()}”.` : ''}`} />
      {outcome && (
        <Card tone="soft" className="flex flex-wrap items-center gap-4">
          <div className="flex -space-x-1.5">{suggested.map((id) => <PartIcon key={id} id={id} size={32} />)}</div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.08em] text-[#be43ff]" style={{ fontWeight: 600 }}>Lykke suggests for “{outcome.title}”</div>
            <div className="text-[15px] text-[#101828]" style={{ fontWeight: 600 }}>{titles(cat, suggested)}</div>
          </div>
          {isSuggested
            ? <span className="inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[12px] text-[#be43ff] shadow-sm" style={{ fontWeight: 600 }}><Check className="h-4 w-4" aria-hidden />In{added.length ? `, plus ${added.length} you added` : ''}</span>
            : <button type="button" onClick={() => set({ parts: withParts(cat, [...new Set([...suggested, ...s.parts])]) }, { field: 'Mix', value: titles(cat, suggested) })} className="h-10 rounded-xl bg-[#be43ff] px-4 text-[13px] text-white transition-all hover:bg-[#a830e0] active:scale-[0.97]" style={{ fontWeight: 600 }}>Use this mix</button>}
        </Card>
      )}

      <H2 note="Each part has its own goal, in that channel’s words">In your campaign</H2>
      {!s.parts.length && <p className="text-[13.5px] text-[#667085]">Nothing yet. Add a part below.</p>}
      <div className="flex flex-col gap-2.5">
        {s.parts.map((id) => {
          const p = cat.parts.find((x) => x.id === id)!; const g = goalOf(cat, s, id);
          const runs = id === 'boost' || id === 'social';
          return (
            <div key={id} className="relative grid gap-3 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm sm:grid-cols-[200px_1fr]">
              <div className="flex items-start gap-2.5">
                <PartIcon id={id} size={32} />
                <div><div className="text-[14px] text-[#101828]" style={{ fontWeight: 600 }}>{p.title}</div><div className="text-[12px] text-[#667085]">{suggested.includes(id) ? 'Lykke’s suggestion' : 'You added'}</div></div>
              </div>
              <div className="flex min-w-0 flex-col gap-2 pr-6">
                <Pills label={`${p.title} goal`} value={g} options={p.goals.map((x) => ({ id: x.id, label: x.label, title: x.why }))}
                  onChange={(v) => set({ goals: { ...s.goals, [id]: v } }, { field: `${p.title} goal`, value: p.goals.find((x) => x.id === v)!.label })} />
                <p className="text-[13px] text-[#667085]">{p.line}</p>
                <p className="text-[12.5px] text-[#101828]" style={{ fontWeight: 600 }}>{priceLine(cat, p.price, runs && s.runs[id as 'boost' | 'social'] === 'own')}</p>
                {runs && (
                  <div className="flex flex-wrap items-center gap-2 text-[12.5px] text-[#667085]">Runs:
                    <Pills label={`Who runs ${p.title}`} value={s.runs[id as 'boost' | 'social']} options={[{ id: 'beatly', label: 'Beatly runs it' }, { id: 'own', label: 'My team runs it' }]}
                      onChange={(v) => set({ runs: { ...s.runs, [id]: v } }, { field: `${p.title} runs`, value: v === 'own' ? 'Your team' : 'Beatly' })} />
                  </div>
                )}
              </div>
              <button type="button" onClick={() => toggle(id)} aria-label={`Remove ${p.title}`} className="absolute right-3 top-3 grid h-7 w-7 place-items-center rounded-full text-[#98A2B3] hover:bg-gray-100 hover:text-[#101828]"><X className="h-4 w-4" aria-hidden /></button>
            </div>
          );
        })}
      </div>

      <button type="button" onClick={() => setOpen((x) => !x)} aria-expanded={open} className="inline-flex h-10 w-fit items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 text-[13px] text-[#344054] shadow-sm transition-colors hover:bg-gray-50" style={{ fontWeight: 600 }}>
        <ChevronDown className={cn('h-4 w-4 transition-transform', open && 'rotate-180')} aria-hidden />Add or change parts
      </button>
      {open && (
        <div className="flex flex-col gap-4">
          {groups.map(([label, parts]) => (
            <div key={label} className="flex flex-col gap-2">
              <span className="text-[10px] uppercase tracking-[0.08em] text-[#667085]" style={{ fontWeight: 600 }}>{label}</span>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {parts.map((p) => {
                  const on = s.parts.includes(p.id);
                  return (
                    <button key={p.id} type="button" onClick={() => toggle(p.id)} aria-pressed={on}
                      className={cn('flex items-start gap-2.5 rounded-xl border bg-white p-3 text-left transition-all', on ? 'border-[#be43ff] bg-[#be43ff]/[0.04] shadow-sm' : 'border-[#eaecf0] hover:border-[#be43ff]/40 hover:bg-[#fcf7ff]')}>
                      <PartIcon id={p.id} size={28} />
                      <span className="min-w-0 flex-1"><span className="block text-[13.5px] text-[#101828]" style={{ fontWeight: 600 }}>{p.title}</span><span className="block text-[12px] leading-snug text-[#667085]">{p.line}</span></span>
                      {on ? <Check className="h-4 w-4 flex-none text-[#be43ff]" aria-hidden /> : <Plus className="h-4 w-4 flex-none text-[#98A2B3]" aria-hidden />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="text-[13px] text-[#667085]"><b className="text-[#101828]">Creator discovery is included.</b> Shortlists checked by a person, for every creator part.</Card>
        <Card className="text-[13px] text-[#667085]"><b className="text-[#101828]">Platform included:</b> Brand OS, Content Hub and rights, {cat.subscription.monthly.toLocaleString('sv-SE')} a month while you have activations.</Card>
      </div>
    </>
  );
}

function priceLine(cat: Catalogue, kind: string, own: boolean): string {
  if (own) return 'Your own ad account: not in this order';
  if (kind === 'budget') return `Your budget, plus ${cat.agencyFeePct}% agency fee`;
  if (kind === 'package') return 'Fixed package price';
  if (kind === 'subscription') return `${cat.subscription.tracking.toLocaleString('sv-SE')} a month on the platform`;
  return 'Fixed study price';
}
